import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiUser, FiSend, FiPaperclip, FiXCircle } from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { useConversationStore } from '../stores/conversationStore';
import ErrorBoundary from '../components/ErrorBoundary';
import FileUpload from '../components/chat/FileUpload';
import MessageAttachment from '../components/chat/MessageAttachment';
import VoiceRecorder from '../components/chat/VoiceRecorder';
import VoiceMessagePlayer from '../components/chat/VoiceMessagePlayer';
import MessageThread from '../components/chat/MessageThread';
import VirtualizedMessageList from '../components/chat/VirtualizedMessageList';
import { logger } from '../utils/logger';

// Use virtualization for conversations with many messages
const VIRTUALIZATION_THRESHOLD = 50;

const ConversationView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { messages, isConnected, error, connect, disconnect, sendMessage } = useConversationStore();
  const [newMessage, setNewMessage] = useState('');
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [replyToMessageId, setReplyToMessageId] = useState<string | null>(null);
  const [aiResponse, setAiResponse] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (id) {
      connect(id);
    }
    return () => {
      disconnect();
    };
  }, [id, connect, disconnect]);

  // Measure container dimensions for virtualization
  useEffect(() => {
    const updateDimensions = () => {
      if (messagesContainerRef.current) {
        const { width, height } = messagesContainerRef.current.getBoundingClientRect();
        setContainerDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Auto-scroll for non-virtualized list
  useEffect(() => {
    if (messages.length < VIRTUALIZATION_THRESHOLD) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (newMessage.endsWith('/ask')) {
      handleAskAI();
    }
  }, [newMessage]);

  const handleAskAI = async () => {
    if (!id || isStreaming) return;

    setIsStreaming(true);
    setAiResponse('');
    
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(`/api/proposal/autofill`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ messages: messages.slice(-5) }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.body) return;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));
            setAiResponse(prev => prev + data);
          }
        }
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        logger.error('Error streaming AI response', error as Error);
        toast.error('Failed to get AI response');
      }
    } finally {
      setIsStreaming(false);
    }
  };

  const cancelAIResponse = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
      setAiResponse('');
    }
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim() && id) {
      sendMessage(id, newMessage, undefined, replyToMessageId || undefined);
      setNewMessage('');
      setReplyToMessageId(null);
    }
  };

  const handleUploadComplete = (fileKey: string, fileName: string) => {
    if (id) {
      const content = `File uploaded: ${fileName}`;
      const metadata = {
        attachment: {
          key: fileKey,
          name: fileName,
        }
      };
      sendMessage(id, content, metadata, replyToMessageId || undefined);
    }
    setShowFileUpload(false);
  };

  const handleRecordingComplete = async (audioBlob: Blob) => {
    if (!id) return;

    const toastId = toast.loading('Uploading and transcribing voice message...');
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice-message.webm');
      formData.append('conversationId', id);

      // This endpoint will handle transcription and message creation
      await api.post('/voice/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Voice message sent!', { id: toastId });
    } catch (error) {
      toast.error('Failed to send voice message.', { id: toastId });
      logger.error('Failed to send voice message', error as Error);
    }
  };

  if (error) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold text-red-500">Failed to load conversation</h2>
        <p className="text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/conversations')}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Back to Conversations
          </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm p-4 flex items-center gap-4" role="banner">
        <button
          onClick={() => navigate('/conversations')}
          className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"
          aria-label="Back to conversations"
          title="Back to conversations"
        >
          <FiArrowLeft size={20} aria-hidden="true" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Conversation</h1>
          <p
            className={`text-xs ${isConnected ? 'text-green-500' : 'text-red-500'}`}
            role="status"
            aria-live="polite"
            aria-label={isConnected ? 'Connection status: Connected' : 'Connection status: Disconnected'}
          >
            {isConnected ? 'Connected' : 'Disconnected'}
          </p>
        </div>
      </header>

      {/* Messages */}
      <main
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto"
        role="main"
        aria-label="Conversation messages"
      >
        <ErrorBoundary
          resetKeys={id ? [id] : []}
          onError={(error, errorInfo) => {
            logger.error('Message thread error', error, { componentStack: errorInfo.componentStack });
            toast.error('Failed to display some messages. Please refresh the page.');
          }}
        >
          {messages.length >= VIRTUALIZATION_THRESHOLD && containerDimensions.height > 0 ? (
            // Use virtualization for large conversations
            <VirtualizedMessageList
              messages={messages}
              conversationId={id!}
              onReply={setReplyToMessageId}
              height={containerDimensions.height}
              width={containerDimensions.width}
            />
          ) : (
            // Standard rendering for small conversations
            <div className="p-6 space-y-4">
              {messages.filter(m => !m.replyToMessageId).map((message) => (
                <MessageThread
                  key={message.id}
                  message={message}
                  allMessages={messages}
                  onReply={setReplyToMessageId}
                  conversationId={id!}
                />
              ))}
            </div>
          )}
        </ErrorBoundary>
        {messages.length < VIRTUALIZATION_THRESHOLD && (
          <>
            {isStreaming && (
              <div className="flex gap-3 px-6 pb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <BiBot className="text-white" size={16} />
                </div>
                <div className="max-w-[70%] p-3 rounded-lg bg-gray-200 text-gray-900">
                  <p className="whitespace-pre-wrap">{aiResponse}</p>
                  <button onClick={cancelAIResponse} className="mt-2 text-xs text-red-500">
                    <FiXCircle className="inline mr-1" />
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </main>

      {/* File Upload Modal */}
      {showFileUpload && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-4">
            <ErrorBoundary
              onError={(error) => {
                logger.error('File upload error', error);
                toast.error('File upload failed. Please try again.');
                setShowFileUpload(false);
              }}
            >
              <FileUpload conversationId={id!} onUploadComplete={handleUploadComplete} />
            </ErrorBoundary>
            <button onClick={() => setShowFileUpload(false)} className="mt-2 text-sm text-gray-500">Cancel</button>
            </div>
        </div>
      )}

      {/* Message Input */}
      <footer className="bg-white p-4" role="contentinfo" aria-label="Message input area">
        {replyToMessageId && (
          <div
            className="text-sm text-gray-500 mb-2"
            role="status"
            aria-live="polite"
            aria-label="Replying to a message"
          >
            Replying to message...
            <button
              onClick={() => setReplyToMessageId(null)}
              className="ml-2 text-red-500 hover:underline focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
              aria-label="Cancel reply"
            >
              Cancel
            </button>
          </div>
        )}
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFileUpload(true)}
            className="p-2 text-gray-500 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            aria-label="Attach file"
            title="Attach file"
          >
            <FiPaperclip aria-hidden="true" />
          </button>
          <label htmlFor="message-input" className="sr-only">
            Type your message
          </label>
          <input
            id="message-input"
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Message input"
            aria-describedby={replyToMessageId ? "reply-status" : undefined}
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:bg-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
            disabled={!newMessage.trim()}
            aria-label="Send message"
            title="Send message"
          >
            <FiSend aria-hidden="true" />
          </button>
          <VoiceRecorder onRecordingComplete={handleRecordingComplete} />
        </form>
      </footer>
    </div>
  );
};

export default ConversationView;
